import { App as AntApp, ConfigProvider, theme } from "antd";
import { Refine } from "@refinedev/core";
import { useNotificationProvider } from "@refinedev/antd";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef } from "react";
import { authProvider } from "./authProvider";
import { dataProvider } from "./dataProvider";
import { useThemeStore } from "../../stores/themeStore";
import { useTenantStore } from "../../stores/tenantStore";
import { getAccountingPalette } from "../theme/accountingTheme";

const isDevelopment = import.meta.env.DEV;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: isDevelopment ? 0 : 30_000,
      gcTime: isDevelopment ? 0 : 5 * 60_000,
      refetchOnMount: isDevelopment ? "always" : undefined,
      refetchOnWindowFocus: isDevelopment
    }
  }
});

export function AppProviders({ children }: { children: ReactNode }) {
  const mode = useThemeStore((x) => x.mode);
  const accent = useThemeStore((x) => x.accent);
  const tenantKey = useTenantStore((x) => `${x.orgId ?? ""}:${x.financialYearId ?? ""}`);
  const lastTenantKey = useRef<string>();
  const palette = getAccountingPalette(accent, mode);

  useEffect(() => {
    if (lastTenantKey.current === undefined) {
      lastTenantKey.current = tenantKey;
      return;
    }
    if (lastTenantKey.current !== tenantKey) {
      lastTenantKey.current = tenantKey;
      queryClient.clear();
    }
  }, [tenantKey]);

  const themeConfig = useMemo(
    () => ({
      algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: {
        colorPrimary: palette.primary,
        colorInfo: palette.info,
        colorSuccess: palette.success,
        colorWarning: palette.warning,
        colorError: palette.error,
        colorBgLayout: palette.appBackground,
        borderRadius: 8,
        fontFamily: "Inter, Segoe UI, Roboto, Arial, sans-serif"
      },
      components: {
        Layout: {
          headerBg: palette.headerBackground,
          siderBg: palette.siderBackground
        },
        Table: {
          headerBg: mode === "dark" ? "#172033" : "#eef3f9",
          rowHoverBg: mode === "dark" ? "#172033" : "#f7fbff"
        }
      }
    }),
    [mode, palette]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={themeConfig}>
        <AntApp>
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            notificationProvider={useNotificationProvider}
            resources={[
              { name: "daily-entries", list: "/daily-entry" },
              { name: "cash-entries", list: "/cash-entry" },
              { name: "statement-entries", list: "/statement-entry" }
            ]}
            options={{ syncWithLocation: true, warnWhenUnsavedChanges: true }}
          >
            {children}
          </Refine>
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
